import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Archive, RefreshCw, CheckCircle2, AlertTriangle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateTime } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/maintenance/article-sanitation`;

const Stat = ({ label, value, ok, testId }) => (
  <div className={`border rounded p-2 ${ok ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
    <div className="text-[11px] text-muted-foreground">{label}</div>
    <div className={`text-2xl font-bold ${ok ? 'text-emerald-800' : 'text-amber-800'}`} data-testid={testId}>{value}</div>
  </div>
);

export const MirrorArchivePanel = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [snapshots, setSnapshots] = useState([]);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreText, setRestoreText] = useState('');

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/archive-preview`, { withCredentials: true });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo analizar');
    } finally { setLoading(false); }
  }, []);

  const loadSnapshots = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/snapshots`, { withCredentials: true });
      setSnapshots(r.data.snapshots || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadPreview(); loadSnapshots(); }, [loadPreview, loadSnapshots]);

  const apply = async () => {
    try {
      const r = await axios.post(`${API}/archive-apply`, { confirm: confirmText }, { withCredentials: true });
      setConfirmOpen(false);
      setConfirmText('');
      toast.success(`Archivados ${r.data.archived} · stock fantasma saneado en ${r.data.phantom_cleared}. Respaldo ${r.data.snapshot_id}`);
      loadPreview();
      loadSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo archivar');
    }
  };

  const restore = async () => {
    try {
      const r = await axios.post(`${API}/restore`, { snapshot_id: restoreTarget.snapshot_id, confirm: restoreText }, { withCredentials: true });
      setRestoreTarget(null);
      setRestoreText('');
      const parts = Object.entries(r.data.restored || {}).map(([k, v]) => `${v} ${k}`).join(', ');
      toast.success(`Restaurado: ${parts || 'sin cambios'}`);
      loadPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo restaurar');
    }
  };

  const eligible = preview?.eligible_count || 0;
  const blocked = preview?.blocked_count || 0;
  const phantom = preview?.phantom_count || 0;
  const archivedPhantom = preview?.archived_phantom_count || 0;
  const clean = preview && eligible === 0 && blocked === 0 && archivedPhantom === 0;

  return (
    <Card className={eligible > 0 || blocked > 0 ? 'border-amber-300' : 'border-emerald-300'} data-testid="mirror-archive-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-amber-600" /> Limpieza de espejos consolidados
            {clean && <Badge className="bg-emerald-500 text-white" data-testid="mirror-archive-clean-badge">Nada que limpiar</Badge>}
          </span>
          <Button size="sm" variant="outline" onClick={() => { loadPreview(); loadSnapshots(); }} disabled={loading} data-testid="mirror-archive-refresh-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Analizar
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Archiva (no borra) los espejos que ya fueron consolidados en corridas anteriores. Es elegible todo espejo que ya
          <b> no tenga lotes ni movimientos</b> apuntándolo. Si alguno arrastra un <b>stock fantasma</b> (contador &gt;0 sin respaldo),
          se pone en 0 al archivar: la mercadería real sigue intacta en la ficha canónica. Primero mostramos la lista y la cantidad;
          recién al confirmar se aplica. Guarda una copia de seguridad recuperable.
        </p>
        {preview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="Listos para archivar" value={eligible} ok={eligible === 0} testId="mirror-archive-eligible" />
            <Stat label="Con stock fantasma (→ 0)" value={phantom} ok={phantom === 0} testId="mirror-archive-phantom" />
            <Stat label="Archivados con fantasma (→ 0)" value={archivedPhantom} ok={archivedPhantom === 0} testId="mirror-archive-archived-phantom" />
            <Stat label="Bloqueados (aún con datos)" value={blocked} ok={blocked === 0} testId="mirror-archive-blocked" />
          </div>
        )}

        {preview?.eligible?.length > 0 && (
          <div>
            <div className="text-xs font-semibold mb-1">Se archivarán:</div>
            <Table data-testid="mirror-archive-eligible-table">
              <TableHeader>
                <TableRow><TableHead>Espejo</TableHead><TableHead>Depósito</TableHead><TableHead className="text-right">Stock</TableHead><TableHead className="text-right">Lotes</TableHead><TableHead className="text-right">Movs</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {preview.eligible.map(a => (
                  <TableRow key={a.article_id} data-testid={`mirror-archive-row-${a.article_id}`}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-xs">{a.warehouse}</TableCell>
                    <TableCell className="text-right">{a.stock}{a.phantom && <Badge className="ml-1 bg-amber-500 text-white">fantasma → 0</Badge>}</TableCell>
                    <TableCell className="text-right">{a.lots}</TableCell>
                    <TableCell className="text-right">{a.movements}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {preview?.blocked?.length > 0 && (
          <div className="text-xs text-rose-700" data-testid="mirror-archive-blocked-note">
            {blocked} espejo(s) NO se tocan porque todavía tienen stock, lotes o movimientos. Requieren consolidarse primero.
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={() => setConfirmOpen(true)} disabled={(eligible === 0 && archivedPhantom === 0) || loading} data-testid="mirror-archive-apply-btn">
            <Archive className="w-4 h-4 mr-1" /> Archivar consolidados
          </Button>
        </div>

        {snapshots.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-xs font-semibold mb-1">Respaldos y recuperación</div>
            <div className="space-y-1" data-testid="mirror-archive-snapshots">
              {snapshots.slice(0, 8).map(s => (
                <div key={s.snapshot_id} className="flex items-center justify-between text-xs border rounded px-2 py-1">
                  <span><code>{s.snapshot_id}</code> · {s.docs} reg · {fmtDateTime(s.created_at)}</span>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => setRestoreTarget(s)} data-testid={`mirror-restore-btn-${s.snapshot_id}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> Restaurar
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="mirror-archive-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar archivado</DialogTitle>
            <DialogDescription className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
              <span>Se archivarán {eligible} espejo(s) consolidado(s) (sin lotes ni movimientos). {phantom > 0 && <>De ellos, <b>{phantom}</b> tienen stock fantasma que se pondrá en 0. </>}{archivedPhantom > 0 && <>Además, <b>{archivedPhantom}</b> ya archivado(s) con stock fantasma también se pondrán en 0. </>}No se borra nada: queda recuperable desde el respaldo. Escribí <b>ARCHIVAR</b> para continuar.</span>
            </DialogDescription>
          </DialogHeader>
          <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="ARCHIVAR" data-testid="mirror-archive-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="mirror-archive-confirm-cancel">Cancelar</Button>
            <Button onClick={apply} disabled={confirmText.trim().toUpperCase() !== 'ARCHIVAR'} data-testid="mirror-archive-confirm-submit">Archivar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!restoreTarget} onOpenChange={o => !o && setRestoreTarget(null)}>
        <DialogContent data-testid="mirror-restore-dialog">
          <DialogHeader>
            <DialogTitle>Restaurar respaldo</DialogTitle>
            <DialogDescription className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <span>Vas a restaurar el respaldo <code>{restoreTarget?.snapshot_id}</code> ({restoreTarget?.docs} registros). Los artículos, lotes y movimientos volverán al estado previo a esa corrida. Escribí <b>RESTAURAR</b> para continuar.</span>
            </DialogDescription>
          </DialogHeader>
          <Input value={restoreText} onChange={e => setRestoreText(e.target.value)} placeholder="RESTAURAR" data-testid="mirror-restore-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} data-testid="mirror-restore-cancel">Cancelar</Button>
            <Button onClick={restore} disabled={restoreText.trim().toUpperCase() !== 'RESTAURAR'} data-testid="mirror-restore-submit">Restaurar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
